import React, { useState, useMemo, useEffect, useCallback } from 'react';
import styled, { css } from '@emotion/native';
import { H3 } from '@/shared/components/StyledText';
import { sand, white, green } from '@/shared/styles/colors';
import { Image } from 'react-native';
import ScrollableView from '@/shared/components/ScrollableView';
import { GET_CONSULTATIONS } from '@/shared/queries';
import { useQuery } from '@apollo/client';
import SessionCard from '@/components/sessions/SessionCard';
import {
  QueryResult,
  ConsultationStatus,
  SessionType,
} from '@/shared/types/generated';
import { FullScreenLoader } from '@/shared/components/FullScreenLoader';
import TabItem from '@/components/sessions/TabItem';
import NoConsultation from '@/components/sessions/NoConsultation';
import BackToTopButton from '@/shared/components/BackToTopButton';
import { useScrollToTop } from '@/shared/hooks/useScrollToTop';
import Spacer from '@/shared/components/Spacer';
import {
  useFocusEffect,
  useLocalSearchParams,
  useGlobalSearchParams,
  router,
  Href,
} from 'expo-router';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import useStore from '@/stores/useStore';
import { ROUTES } from '@/shared/constants/routes';
import { useClearRedirectState } from '@/shared/hooks/useClearRedirectState';
import StaticInput from '@/shared/components/StaticInput';
import { TextInput } from 'react-native-paper';
import { IS_WEB } from '@/shared/constants/utility';

const Sessions = () => {
  const { isStylist, setIsPostCallModalVisible } = useStore();
  const { isDesktop } = useBreakpoint();
  // On mobile, useGlobalSearchParams is more reliable for reading params set via setParams
  // On web, useLocalSearchParams works fine with query strings
  const localParams = useLocalSearchParams();
  const globalParams = useGlobalSearchParams();
  const rawParams = IS_WEB ? localParams : globalParams;
  const { clearRedirectState } = useClearRedirectState();

  // Normalize params that can come through as arrays
  const showPostCall = Array.isArray(rawParams.showPostCall)
    ? rawParams.showPostCall[0]
    : rawParams.showPostCall;
  const consultationId = Array.isArray(rawParams.id)
    ? rawParams.id[0]
    : rawParams.id;
  const tabParam = rawParams.tab;
  const [autoOpenDetailId, setAutoOpenDetailId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Store current tab in state as fallback for mobile when params might be lost
  const [currentTabState, setCurrentTabState] = useState<string | undefined>(
    undefined,
  );
  const { data, loading, refetch } =
    useQuery<QueryResult<'consultations'>>(GET_CONSULTATIONS);

  const scrollToTop = useScrollToTop();

  // Derive selectedStatus from query param, defaulting to Upcoming
  // On mobile, use state as fallback if param is lost
  const selectedStatus = useMemo<ConsultationStatus>(() => {
    const tab = Array.isArray(tabParam) ? tabParam[0] : tabParam;
    const effectiveTab = tab || (IS_WEB ? undefined : currentTabState);

    if (!effectiveTab) {
      return ConsultationStatus.Upcoming;
    }
    // Normalize the tab param to match ConsultationStatus enum values
    const normalizedTab = effectiveTab.toLowerCase();
    if (normalizedTab === 'pending') {
      return ConsultationStatus.Pending;
    }
    if (normalizedTab === 'completed') {
      return ConsultationStatus.Completed;
    }
    if (normalizedTab === 'cancelled' || normalizedTab === 'canceled') {
      return ConsultationStatus.Cancelled;
    }
    if (normalizedTab === 'upcoming') {
      return ConsultationStatus.Upcoming;
    }
    return ConsultationStatus.Upcoming;
  }, [tabParam, currentTabState]);

  // Sync state with URL params on mobile
  useEffect(() => {
    if (!IS_WEB) {
      const tab = Array.isArray(tabParam) ? tabParam[0] : tabParam;
      if (tab) {
        setCurrentTabState(tab);
      }
    }
  }, [tabParam]);

  // Helper function to update the tab query parameter
  const handleTabChange = useCallback((status: ConsultationStatus) => {
    const tabValue =
      status === ConsultationStatus.Cancelled
        ? 'cancelled'
        : status.toLowerCase();
    if (IS_WEB) {
      router.push(`${ROUTES.SESSIONS.path}?tab=${tabValue}` as Href);
    } else {
      // On mobile, use setParams to update params on current route
      // Also update local state as fallback
      setCurrentTabState(tabValue);
      router.setParams({ tab: tabValue });
    }
  }, []);

  // Handle post-call modal display from navigation
  useEffect(() => {
    if (showPostCall === 'true') {
      setIsPostCallModalVisible(true);
      // Clean URL by replacing without the query parameter
      const currentTab = Array.isArray(tabParam)
        ? tabParam[0]
        : tabParam || currentTabState;
      if (IS_WEB) {
        const url = currentTab
          ? `${ROUTES.SESSIONS.path}?tab=${currentTab}`
          : ROUTES.SESSIONS.path;
        router.replace(url as Href);
      } else {
        // On mobile, preserve tab param if it exists
        if (currentTab) {
          setCurrentTabState(currentTab);
          router.setParams({ tab: currentTab });
        }
      }
    }
  }, [showPostCall, setIsPostCallModalVisible, tabParam, currentTabState]);

  // Handle deep linking to specific consultation detail
  useEffect(() => {
    if (consultationId && data?.consultations) {
      const normalizedConsultationId =
        typeof consultationId === 'string'
          ? consultationId
          : String(consultationId || '');
      const targetConsultation = data.consultations.find(
        (consultation) =>
          consultation.id.toString() === normalizedConsultationId,
      );

      if (targetConsultation) {
        // Determine the appropriate tab based on consultation status
        let targetTab: string;
        if (
          targetConsultation.status === ConsultationStatus.Pending &&
          targetConsultation.sessionType === SessionType.InPersonSession
        ) {
          targetTab = ConsultationStatus.Pending.toLowerCase();
        } else if (targetConsultation.status === ConsultationStatus.Completed) {
          targetTab = 'completed';
        } else if (
          [
            ConsultationStatus.Cancelled,
            ConsultationStatus.ClientNoShow,
            ConsultationStatus.StylistNoShow,
          ].includes(targetConsultation.status)
        ) {
          targetTab = 'cancelled';
        } else {
          targetTab = 'upcoming';
        }

        // Update URL with both tab and id params
        const normalizedConsultationId =
          typeof consultationId === 'string'
            ? consultationId
            : String(consultationId || '');
        if (IS_WEB) {
          router.replace(
            `${ROUTES.SESSIONS.path}?tab=${targetTab}&id=${normalizedConsultationId}` as Href,
          );
        } else {
          // On mobile, use setParams to update params on current route
          // Also update local state as fallback
          setCurrentTabState(targetTab);
          router.setParams({
            tab: targetTab,
            id: normalizedConsultationId,
          });
        }

        // Set the consultation ID to auto-open
        setAutoOpenDetailId(normalizedConsultationId);

        clearRedirectState();
      }
    }
  }, [clearRedirectState, consultationId, data?.consultations]);

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch]),
  );

  const onDetailModalClose = () => {
    setAutoOpenDetailId(null);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const filteredConsultations = useMemo(() => {
    const consultations = data?.consultations || [];

    // First filter by status
    let statusFilteredConsultations: typeof consultations = [];

    if (selectedStatus === ConsultationStatus.Pending) {
      // Filter for pending in-person session requests only (excludes instant styling and event sessions)
      statusFilteredConsultations = consultations
        .filter(
          (consultation) =>
            consultation.status === ConsultationStatus.Pending &&
            consultation.sessionType === SessionType.InPersonSession,
        )
        .sort((a, b) => {
          // Sort by startTime ascending (earliest requests first)
          const dateA = new Date(a.startTime).getTime();
          const dateB = new Date(b.startTime).getTime();
          return dateA - dateB;
        });
    } else if (selectedStatus === ConsultationStatus.Cancelled) {
      statusFilteredConsultations = consultations.filter((consultation) =>
        [
          ConsultationStatus.Cancelled,
          ConsultationStatus.ClientNoShow,
          ConsultationStatus.StylistNoShow,
        ].includes(consultation.status),
      );
    } else if (selectedStatus === ConsultationStatus.Upcoming) {
      // Include in progress consultations in the Upcoming tab
      const inProgressOrUpcomingConsultations = consultations.filter(
        (consultation) =>
          [ConsultationStatus.InProgress, ConsultationStatus.Upcoming].includes(
            consultation.status,
          ),
      );

      // Sort so in progress consultations appear at the top
      statusFilteredConsultations = inProgressOrUpcomingConsultations.sort(
        (a, b) => {
          if (
            a.status === ConsultationStatus.InProgress &&
            b.status !== ConsultationStatus.InProgress
          ) {
            return -1;
          }
          if (
            b.status === ConsultationStatus.InProgress &&
            a.status !== ConsultationStatus.InProgress
          ) {
            return 1;
          }
          return 0;
        },
      );
    } else if (selectedStatus === ConsultationStatus.Completed) {
      const completedConsultations = consultations.filter(
        (consultation) => consultation.status === selectedStatus,
      );
      // Sort completed consultations by endTime descending (most recent first)
      statusFilteredConsultations = completedConsultations.sort((a, b) => {
        const dateA = new Date(a.endTime).getTime();
        const dateB = new Date(b.endTime).getTime();
        return dateB - dateA; // Descending order (newest first)
      });
    } else {
      statusFilteredConsultations = consultations.filter(
        (consultation) => consultation.status === selectedStatus,
      );
    }

    // Then apply search filter if there's a search query
    if (!searchQuery.trim()) {
      return statusFilteredConsultations;
    }

    // Remove trailing punctuation from the search query
    const sanitizedQuery = searchQuery
      .toLowerCase()
      .trim()
      .replace(/[.,!?;:]*$/, '');

    return statusFilteredConsultations.filter((consultation) => {
      // Get the other user (if current user is stylist, get client; if client, get stylist)
      const otherUser = isStylist
        ? consultation.client?.user
        : consultation.stylist?.user;
      if (!otherUser) return false;

      const fullName =
        `${otherUser.firstName} ${otherUser.lastName}`.toLowerCase();
      const firstName = otherUser.firstName.toLowerCase();
      const lastName = otherUser.lastName.toLowerCase();

      return (
        fullName.startsWith(sanitizedQuery) ||
        firstName.startsWith(sanitizedQuery) ||
        lastName.startsWith(sanitizedQuery)
      );
    });
  }, [data?.consultations, selectedStatus, searchQuery, isStylist]);

  const hasConsultations = filteredConsultations.length > 0;

  if (loading) {
    return <FullScreenLoader />;
  }

  return (
    <ScrollableOuterContainer isDesktop={isDesktop}>
      {!isDesktop && (
        <StyledH3 isDesktop={isDesktop}>
          {isStylist ? 'All sessions' : 'Upcoming \n& past sessions'}
        </StyledH3>
      )}

      <TabsAndSearchContainer isDesktop={isDesktop}>
        <TabRow isDesktop={isDesktop} isStylist={!!isStylist}>
          {/* Pending tab - only visible for stylists */}
          {isStylist && (
            <TabItem
              isStylist={Boolean(isStylist)}
              key={ConsultationStatus.Pending}
              isLastItem={false}
              selected={selectedStatus === ConsultationStatus.Pending}
              onPress={() => handleTabChange(ConsultationStatus.Pending)}
              text="Pending"
            />
          )}
          {[
            ConsultationStatus.Upcoming,
            ConsultationStatus.Completed,
            ConsultationStatus.Cancelled,
          ].map((status) => (
            <TabItem
              isStylist={Boolean(isStylist)}
              key={status}
              isLastItem={status === ConsultationStatus.Cancelled}
              selected={selectedStatus === status}
              onPress={() => handleTabChange(status)}
              text={
                status === ConsultationStatus.Cancelled ? 'Canceled' : status
              } // fix to use American English spelling on frontend
            />
          ))}
        </TabRow>

        <SearchContainer isDesktop={isDesktop}>
          <StaticInput
            labelText="Search by name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.nativeEvent.text)}
            customStyles={searchStyles}
            placeholderText="Search by name"
            left={
              <TextInput.Icon
                style={{ marginRight: 0 }}
                icon={require('@/assets/images/Search.png')}
                size={18}
              />
            }
            right={
              searchQuery ? (
                <TextInput.Icon
                  style={{ marginRight: 0 }}
                  icon={() => (
                    <ClearIcon source={require('@/assets/images/Clear.png')} />
                  )}
                  size={18}
                  onPress={handleClearSearch}
                />
              ) : null
            }
          />
        </SearchContainer>
      </TabsAndSearchContainer>

      {hasConsultations ? (
        <>
          {filteredConsultations.map((consultation) => (
            <SessionCard
              key={consultation.id}
              consultation={consultation}
              autoOpenDetail={autoOpenDetailId === consultation.id.toString()}
              onDetailModalClose={onDetailModalClose}
              onRequestHandled={
                selectedStatus === ConsultationStatus.Pending
                  ? refetch
                  : undefined
              }
            />
          ))}
          <Spacer height={49} />
          {!isDesktop && (
            <>
              <BackToTopButton scrollToTop={scrollToTop} />
              <Spacer height={20} />
            </>
          )}
        </>
      ) : (
        <NoConsultation status={selectedStatus} />
      )}
    </ScrollableOuterContainer>
  );
};

export default Sessions;

const containerStyle = css({
  flex: 1,
  paddingTop: 28,
  padding: 20,
  backgroundColor: sand,
});

const ScrollableOuterContainer = styled(ScrollableView)(
  ({ isDesktop }: { isDesktop: boolean }) => ({
    ...containerStyle,
    paddingHorizontal: isDesktop ? 50 : 20,
    backgroundColor: isDesktop ? white : sand,
  }),
);

const StyledH3 = styled(H3)<{ isDesktop: boolean }>(({ isDesktop }) => ({
  textAlign: isDesktop ? 'left' : 'center',
  textTransform: 'uppercase',
  marginBottom: 20,
  alignSelf: isDesktop ? 'flex-start' : 'center',
}));

const TabsAndSearchContainer = styled.View(
  ({ isDesktop }: { isDesktop: boolean }) => ({
    flexDirection: isDesktop ? 'row' : 'column',
    justifyContent: isDesktop ? 'space-between' : 'center',
    alignItems: isDesktop ? 'flex-start' : 'center',
    width: '100%',
    marginBottom: 24,
  }),
);

const TabRow = styled.View(
  ({ isDesktop, isStylist }: { isDesktop: boolean; isStylist: boolean }) => ({
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignSelf: isDesktop ? 'flex-end' : 'center',
    paddingBottom: isDesktop ? 8 : 0,
    marginBottom: isDesktop ? 0 : 24,
    // the dreaded nested ternary operator
    paddingHorizontal: isDesktop ? 0 : isStylist ? 0 : 40,
    width: isDesktop ? (isStylist ? 360 : 270) : 'auto',
  }),
);

const SearchContainer = styled.View(
  ({ isDesktop }: { isDesktop: boolean }) => ({
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: isDesktop ? 'flex-end' : 'center',
    width: isDesktop ? 242 : '100%',
  }),
);

const searchStyles = css({
  height: 42,
  fontSize: 12,
  fontWeight: 'bold',
  color: green,
  width: '100%',
  paddingLeft: 0,
});

const ClearIcon = styled(Image)({
  width: 30,
  height: 30,
  resizeMode: 'contain',
});
